// Global style override for disabled button text color
import { Global } from '@emotion/react';
import {
  Button,
  CircularProgress,
  Stack,
  Typography,
  Card,
  CardContent,
  Divider,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Link,
} from "@mui/material";

import { useCart } from "../../contexts/cartContext";
import { useAccount } from "../../contexts/accountContext";
import { usePopup } from "../../contexts/popupContext";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoLockClosedOutline,
  IoTrashOutline,
  IoChevronDownOutline,
} from "react-icons/io5";

import { useTranslation } from 'react-i18next';
import CheckoutStepperHeader from "../../components/checkout/CheckoutStepperHeader";
import TermsDialog from "../../components/terms/TermsDialog";
import { NexApi } from 'src/lib/neuroexchangeApi';
import { useLocalStorageState } from 'src/hooks/useLocalStorageState';
import { createAmountFormater, formatMetricNumber } from 'src/lib/formating';
import CardCheckoutPopup from 'src/components/cardCheckoutPopup/cardCheckoutPopup';

// --- REAL DATA HELPERS ---
function useProjectStats() {
  const [statsMap, setStatsMap] = useState({});
  const [loading, setLoading] = useState(true);

  return { statsMap, loading };
}

const calculateRealMetrics = (staticProject, statsMap) => {
  const quantity = staticProject.series?.[0]?.quantity || 0;
  const price = staticProject.series?.[0]?.price || 0;
  const totalValue = quantity * price;
  const totalPurchased = 0;
  const valueLeft = Math.max(0, totalValue - totalPurchased);
  const rawPercentage = totalValue > 0 ? (totalPurchased / totalValue) * 100 : 0;
  let formattedProgress = "0";
  if (rawPercentage > 0 && rawPercentage < 1) {
    formattedProgress = rawPercentage.toFixed(2);
  } else if (rawPercentage >= 1) {
    formattedProgress = Math.floor(rawPercentage).toString();
  }
  let status = "active";
  if (valueLeft <= 0 && totalValue > 0) status = 'Complete';
  return {
    status,
    total: totalValue,
    raised: totalPurchased,
    available: valueLeft,
    progressLabel: formattedProgress,
    progressVisual: Math.min(100, Math.max(0, rawPercentage))
  };
};

export default function CheckoutPage() {
  // ...existing code...
  const cartContext = useCart();
  const accountContext = useAccount();
  const [pixChecked, setPixChecked] = useState(false);
  // If you want to add a crypto checkbox, add: const [cryptoChecked, setCryptoChecked] = useState(false);
  const popupContext = usePopup();
  const navigate = useNavigate();
  const [hasVerifiedContents, setHasVerifiedContents] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [expandedTokenId, setExpandedTokenId] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [checkoutId, setCheckoutId] = useLocalStorageState("neuropay.checkout.id", null);
  const [termsContext, setTermsContext] = useState({ clientName: '', projectTitle: '', taxId: '' });
  const { t, i18n } = useTranslation('common');
  const { statsMap } = useProjectStats();

  const [payWithCard, setPayWithCard] = useState(false);

  const itemsLabel = (items) => items.map(i => (i.displayName || i.name)).join(", ");

  const renderCheckout = () => {
    const items = cartContext.cartItems;
    const subtotal = cartContext.totalCost;
    const total = subtotal;

    const isProceedDisabled =
      isRedirecting || items.length === 0 || !hasVerifiedContents || !hasAcceptedTerms;

    return (
      <Box sx={{ width: "100%", maxWidth: 1120 }}>
        <Stack spacing={3}>
          {/* TOP HEADER CARD: title + stepper (reusable) */}
          <CheckoutStepperHeader step={0} />

          {/* MAIN STACK OF CARDS */}
          <Stack spacing={2.5}>
            {/* YOUR ORDERS */}
            <Card
              sx={{
                borderRadius: 3,
                bgcolor: "#FFFFFF",
                boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.12)",
              }}
            >
              <CardContent sx={{ px: 3.5, py: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 0.5, color: "#111827" }}
                >
                  {t("checkout.yourOrders", "Your orders")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2.5, color: "#6B7280" }}
                >
                  {t(
                    "checkout.yourOrdersSubtitle",
                    "Review your selected projects"
                  )}
                </Typography>

                {/* ORDER LINES */}
                <Stack spacing={1.25} sx={{ mb: 2.5 }}>
                  {items.length === 0 && (
                    <Box sx={{ display: "flex", alignItems: "baseline" }}>
                      <Typography variant="body2" sx={{ color: "#6B7280" }}>
                        {t("checkout.cartEmpty", "Your cart is empty.")}
                      </Typography>
                      <Link
                        underline="hover"
                        variant="body2"
                        onClick={(e) => { e.preventDefault(); navigate("/explore"); }}
                        sx={{ ml: 1, color: "#2563EB", cursor: "pointer" }}
                      >
                        {t("checkout.browseProducts", "Browse products")}
                      </Link>
                    </Box>
                  )}

                  {items.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: { xs: "flex", sm: "flex" },
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "center" },
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        border: "1px solid #E5E7EB",
                        bgcolor: "#F9FAFB",
                      }}
                    >
                      {(() => {
                        const baseValue = Number(item.value || 0);
                        const premiumValue = Number(item.premium || 0);
                        const unitTotal = cartContext.getItemUnitPrice?.(item) || (baseValue + premiumValue);
                        const lineSubtotal = unitTotal * Number(item.count || 0);
                        return (
                          <>
                            {/* Top row (xs): name + issuer; Left (sm+): same */}
                            <Box sx={{ flex: "1 1 0", minWidth: 0, mb: { xs: 1, sm: 0 } }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#111827",
                                  mt: 0.5,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {item.displayName || item.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#6B7280", fontSize: 11 }}
                              >
                                {item.issuer
                                  ? t("checkout.byIssuer", { issuer: item.issuer })
                                  : null}
                              </Typography>
                              <Typography variant="caption" sx={{ color: "#6B7280", display: 'block', fontSize: 11 }}>
                                {t('checkout.value', 'Value')}: {createAmountFormater(item.currency, i18n.language)(baseValue)}
                              </Typography>
                              {premiumValue > 0 ? (
                                <Typography variant="caption" sx={{ color: "#6B7280", display: 'block', fontSize: 11 }}>
                                  {t('checkout.premium', 'Premium')}: {createAmountFormater(item.currency, i18n.language)(premiumValue)}
                                </Typography>
                              ) : null}
                              <Typography variant="caption" sx={{ color: "#111827", display: 'block', fontWeight: 600, fontSize: 11 }}>
                                {t('checkout.subtotal', 'Subtotal')}: {createAmountFormater(item.currency, i18n.language)(lineSubtotal)}
                              </Typography>
                            </Box>

                            {/* Bottom row (xs): qty + price + trash; Right row (sm+): same */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: { xs: "100%", sm: "auto" },
                                justifyContent: { xs: "space-between", sm: "flex-end" },
                              }}
                            >
                              {/* Quantity pill */}
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "stretch",
                                  borderRadius: 999,
                                  border: "1px solid #E5E7EB",
                                  overflow: "hidden",
                                  height: 32,
                                  bgcolor: "#FFFFFF",
                                }}
                              >
                                {(() => {
                                  const effectiveMax = cartContext.getEffectiveMaxForItem?.(item);
                                  return (
                                    <>
                                      {/* <Button
                                  onClick={() => cartContext.updateItemCount(item.id, item.count - 1)}
                                  disabled={item.count <= 1}
                                  sx={{
                                    minWidth: 32,
                                    px: 0,
                                    borderRight: "1px solid #E5E7EB",
                                    borderRadius: 0,
                                    color: "#6B7280",
                                  }}
                                >
                                  <IoRemove size={14} />
                                </Button> */}
                                      <Box
                                        sx={{
                                          px: 1,
                                          minWidth: 26,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{ fontWeight: 600, fontSize: 12, color: "#111827" }}
                                        >
                                          {formatMetricNumber(item.count, 0)}
                                        </Typography>
                                      </Box>
                                      {/* <Button
                                  onClick={() => cartContext.updateItemCount(item.id, item.count + 1)}
                                  disabled={effectiveMax !== undefined && Number(item.count || 0) >= effectiveMax}
                                  sx={{
                                    minWidth: 32,
                                    px: 0,
                                    borderLeft: "1px solid #E5E7EB",
                                    borderRadius: 0,
                                    color: "#6B7280",
                                  }}
                                >
                                  <IoAdd size={14} />
                                </Button> */}
                                    </>
                                  );
                                })()}
                              </Box>

                              {/* Price */}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#111827",
                                  minWidth: 80,
                                  textAlign: "right",
                                }}
                              >
                                {createAmountFormater(item.currency, i18n.language)((cartContext.getItemUnitPrice?.(item) || Number(item.value || 0) + Number(item.premium || 0)) * item.count)}
                              </Typography>

                              {/* Trash */}
                              <Button
                                onClick={() => cartContext.removeFromCart(item)}
                                sx={{
                                  minWidth: 32,
                                  px: 0,
                                  color: "#9CA3AF",
                                  "&:hover": { color: "#374151", bgcolor: "#F3F4F6" },
                                }}
                              >
                                <IoTrashOutline size={16} />
                              </Button>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  ))}
                </Stack>

                {/* SUMMARY */}
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography
                      variant="caption"
                      sx={{ color: "#6B7280", fontSize: 12 }}
                    >
                      {t("checkout.subtotal", "Subtotal")}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#6B7280", fontSize: 12 }}
                    >
                      {createAmountFormater(items[0]?.currency, i18n.language)(subtotal)}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#111827" }}
                  >
                    {t("checkout.total", "Total")}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#111827" }}
                  >
                    {createAmountFormater(items[0]?.currency, i18n.language)(total)}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* TOKEN CONTENTS CARD */}
            <Card
              sx={{
                borderRadius: 3,
                bgcolor: "#FFFFFF",
                boxShadow: "0px 16px 40px rgba(15, 23, 42, 0.08)",
              }}
            >
              <CardContent sx={{ px: 3.5, py: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 0.5, color: "#111827" }}
                >
                  {t("checkout.tokenContents", "Token contents")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2.5, color: "#6B7280" }}
                >
                  {t(
                    "checkout.tokenContentsSubtitle",
                    "Verify the contents of all tokens"
                  )}
                </Typography>

                {/* Accordion list */}
                <Box
                  sx={{
                    mb: 2.5,
                    borderRadius: 2.5,
                    bgcolor: "#F3F4F6",
                    p: 1,
                  }}
                >
                  {items.map((item, index) => {
                    const isExpanded = expandedTokenId === item.id;

                    // Use real token metadata structure from projects.json
                    // Resolve token contents from item payload when present
                    const tokenContents = item.tokenContent || item.details || {};

                    const rows = [
                      { key: 'Project_Id', label: 'Project_Id', value: tokenContents.Project_Id },
                      { key: 'Project_Label', label: 'Project_Label', value: tokenContents.Project_Label },
                      // { key: 'Coffee_ProducerName', label: 'Coffee_ProducerName', value: tokenContents.Coffee_ProducerName },
                      // { key: 'Coffee_ProducerId', label: 'Coffee_ProducerId', value: tokenContents.Coffee_ProducerId },
                      // { key: 'Coffee_CPR_Number', label: 'Coffee_CPR_Number', value: tokenContents.Coffee_CPR_Number },
                      // { key: 'Coffee_ProductDescription', label: 'Coffee_ProductDescription', value: tokenContents.Coffee_ProductDescription },
                      // { key: 'Coffee_Sieve', label: 'Coffee_Sieve', value: tokenContents.Coffee_Sieve },
                      // { key: 'Coffee_QualityStandard', label: 'Coffee_QualityStandard', value: tokenContents.Coffee_QualityStandard },
                      // { key: 'Coffee_HarvestSeason', label: 'Coffee_HarvestSeason', value: tokenContents.Coffee_HarvestSeason },
                      // { key: 'Coffee_TotalBagsInCPR', label: 'Coffee_TotalBagsInCPR', value: formatNumber(tokenContents.Coffee_TotalBagsInCPR) },
                      // { key: 'Coffee_IndexName', label: 'Coffee_IndexName', value: tokenContents.Coffee_IndexName },
                      // { key: 'Coffee_BasePricePerBag', label: 'Coffee_BasePricePerBag', value: formatCurrencyBRL(tokenContents.Coffee_BasePricePerBag) },
                      // { key: 'Coffee_PremiumPerBag', label: 'Coffee_PremiumPerBag', value: formatCurrencyBRL(tokenContents.Coffee_PremiumPerBag) },
                      // { key: 'Coffee_InitialCPRValue', label: 'Coffee_InitialCPRValue', value: formatCurrencyBRL(tokenContents.Coffee_InitialCPRValue) },
                      // { key: 'Coffee_CurrencyCode', label: 'Coffee_CurrencyCode', value: tokenContents.Coffee_CurrencyCode },
                      // { key: 'Coffee_MaturityDate', label: 'Coffee_MaturityDate', value: formatDate(tokenContents.Coffee_MaturityDate) },
                      // { key: 'Coffee_HasAgriculturalInsurance', label: 'Coffee_HasAgriculturalInsurance', value: tokenContents.Coffee_HasAgriculturalInsurance },
                      // { key: 'Coffee_HasPersonalEndorsement', label: 'Coffee_HasPersonalEndorsement', value: tokenContents.Coffee_HasPersonalEndorsement },
                      // { key: 'Coffee_Notes', label: 'Coffee_Notes', value: tokenContents.Coffee_Notes },
                      // { key: 'Asset_HasCollateral', label: 'Asset_HasCollateral', value: tokenContents.Asset_HasCollateral },
                      // { key: 'Asset_CollateralReference', label: 'Asset_CollateralReference', value: tokenContents.Asset_CollateralReference },
                    ].filter(r => r.value !== undefined && r.value !== null && r.value !== '');

                    return (
                      <Box
                        key={`contents-${item.id}`}
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          mb: 1,
                          border: "1px solid #E5E7EB",
                          bgcolor: "#F9FAFB",
                          "&:last-of-type": { mb: 0 },
                        }}
                      >
                        {/* Summary row */}
                        <Box
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          onClick={() => setExpandedTokenId(isExpanded ? null : item.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setExpandedTokenId(isExpanded ? null : item.id);
                            }
                          }}
                          sx={{
                            px: 2,
                            py: 1.25,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#111827",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.displayName || item.name}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              ml: 1,
                              transition: "transform 150ms ease",
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              color: "#6B7280",
                            }}
                          >
                            <IoChevronDownOutline size={16} />
                          </Box>
                        </Box>

                        {/* Details table */}
                        {isExpanded && (
                          <Box
                            sx={{
                              px: 2,
                              pb: 1.5,
                              pt: 0.75,
                              bgcolor: "#F3F4F6",
                            }}
                          >
                            {rows.length === 0 ? (
                              <Typography
                                variant="caption"
                                sx={{ color: "#6B7280" }}
                              >
                                {t(
                                  "checkout.noTokenMetadata",
                                  "No token metadata available yet."
                                )}
                              </Typography>
                            ) : (
                              <Table
                                size="small"
                                sx={{
                                  "& td": {
                                    borderBottom: "1px solid #E5E7EB",
                                    py: 0.75,
                                  },
                                  "& tr:last-of-type td": {
                                    borderBottom: "none",
                                  },
                                }}
                              >
                                <TableBody>
                                  {rows.map(({ key, label, value }) => (
                                    <TableRow key={key}>
                                      <TableCell
                                        sx={{
                                          width: "35%",
                                          pr: 2,
                                          fontSize: 12,
                                          color: "#6B7280",
                                          fontWeight: 500,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {label}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontSize: 12,
                                          color: "#111827",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {String(value)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>

                {/* Checkbox line */}
                {/* Pix checkbox removed; only verify contents checkbox remains */}
                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <input
                    id="verify-contents"
                    type="checkbox"
                    checked={hasVerifiedContents}
                    onChange={(e) =>
                      setHasVerifiedContents(e.target.checked)
                    }
                    style={{
                      marginTop: 3,
                      width: 16,
                      height: 16,
                      cursor: "pointer",
                    }}
                  />
                  <label
                    htmlFor="verify-contents"
                    style={{
                      fontSize: 13,
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    {t(
                      "checkout.verifyContentsCheck",
                      "I have verified and approved the content of all tokens I am about to purchase."
                    )}
                  </label>
                </Stack>
              </CardContent>
            </Card>


            {/* TERMS & CONDITIONS CARD */}
            <Card
              sx={{
                borderRadius: 3,
                bgcolor: "#FFFFFF",
                boxShadow: "0px 16px 40px rgba(15, 23, 42, 0.08)",
              }}
            >
              <CardContent sx={{ px: 3.5, py: 3 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mb: 0.5, color: "#111827" }}
                >
                  {t("checkout.termsTitle", "Terms & Conditions")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2.5, color: "#6B7280" }}
                >
                  {t(
                    "checkout.termsSubtitle",
                    "Please review and accept the terms and conditions"
                  )}
                </Typography>

                {/* Simple “dropdown” look for terms link */}
                <Box
                  sx={{
                    mb: 2.5,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2,
                    border: "1px solid #E5E7EB",
                    bgcolor: "#F9FAFB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    // Open terms in a dialog to keep users inside checkout
                    const first = (cartContext?.cartItems || [])[0] || null;
                    const project = null;

                    const clientName = first?.issuer || first?.projectTitle || '';

                    const taxId = '';

                    const projectTitle = first?.projectTitle || '';

                    // Debug buyer/company name resolution (opt-in)
                    try {
                      const sp = new URLSearchParams(window.location.search);
                      const debugTerms = sp.get('debugTerms') === '1' || window.localStorage?.getItem('debugTerms') === '1';
                      if (debugTerms) {
                        console.info('[terms][checkout] open dialog', {
                          buyerName: String(accountContext?.username || '').trim(),
                          isLoggedIn: !!accountContext?.isLoggedIn,
                          isQuickLoggedIn: !!accountContext?.isQuickLoggedIn,
                          companyClientName: String(clientName || '').trim(),
                          projectTitle: String(projectTitle || '').trim(),
                          cartItemProjectId: first?.projectId,
                        });
                      }
                    } catch (_) {
                      // ignore
                    }

                    setTermsContext({
                      clientName: clientName || '',
                      projectTitle: projectTitle || '',
                      taxId: taxId || '',
                    });
                    setTermsOpen(true);
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#111827", fontWeight: 500 }}
                  >
                    {t(
                      "checkout.termsLinkLabel",
                      "Innova marketplace Terms & Conditions"
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#6B7280", fontSize: 11 }}
                  >
                    {t("checkout.view", "View")}
                  </Typography>
                </Box>

                <Stack direction="row" alignItems="flex-start" spacing={1}>
                  <input
                    id="accept-terms"
                    type="checkbox"
                    checked={hasAcceptedTerms}
                    onChange={(e) =>
                      setHasAcceptedTerms(e.target.checked)
                    }
                    style={{
                      marginTop: 3,
                      width: 16,
                      height: 16,
                      cursor: "pointer",
                    }}
                  />
                  <label
                    htmlFor="accept-terms"
                    style={{
                      fontSize: 13,
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    {t(
                      "checkout.acceptTermsCheck",
                      "I have read and approved Innova’s marketplace terms and conditions."
                    )}
                  </label>
                </Stack>

                <TermsDialog
                  open={termsOpen}
                  onClose={() => setTermsOpen(false)}
                  onAccept={() => {
                    setHasAcceptedTerms(true);
                    setTermsOpen(false);
                  }}
                  language={i18n?.resolvedLanguage || i18n?.language}
                  buyerName={
                    accountContext?.username ||
                    window.sessionStorage?.getItem('agent-user-Username') ||
                    window.sessionStorage?.getItem('username') ||
                    ''
                  }
                  clientName={termsContext.clientName || t('termsPage.defaultClientName', 'Innova')}
                  projectTitle={termsContext.projectTitle}
                  taxId={termsContext.taxId}
                />
              </CardContent>
            </Card>

            {/* BOTTOM BUTTON BAR */}
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              sx={{ mt: 1 }}
            >
              <Button
                variant="outlined"
                disabled={isRedirecting}
                sx={{
                  minWidth: 160,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#FFFFFF",
                  borderColor: "#E5E7EB",
                  color: "#111827",
                  "&:hover": {
                    borderColor: "#D1D5DB",
                    bgcolor: "#F9FAFB",
                  },
                }}
                onClick={() => {
                  // Reset all relevant state and close popup, then navigate home
                  setHasVerifiedContents(false);
                  setHasAcceptedTerms(false);
                  setExpandedTokenId(null);
                  setIsRedirecting(false);
                  popupContext.forceClose();
                  navigate("/");
                }}
              >
                {t("checkout.cancel", "Cancel")}
              </Button>
              <Button
                variant="contained"
                disabled={isProceedDisabled}
                sx={{
                  minWidth: 220,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: isProceedDisabled ? "#e0e7ef" : "#2563EB",
                  color: isProceedDisabled ? "#374151" : "#fff",
                  boxShadow: isProceedDisabled
                    ? "none"
                    : "0px 18px 40px rgba(37, 99, 235, 0.45)",
                  border: isProceedDisabled ? "1px solid #c3d0e0" : undefined,
                  opacity: isProceedDisabled ? 0.95 : 1,
                  "&:hover": !isProceedDisabled
                    ? {
                      bgcolor: "#1D4ED8",
                      boxShadow:
                        "0px 22px 50px rgba(37, 99, 235, 0.55)",
                    }
                    : {},
                }}
                onClick={() => {
                  // Move out of the NeuroPay (card) popup flow into the alternative payment methods screen.
                  popupContext.forceClose();
                  navigate('/checkout/method');
                }}
              >
                {t('checkout.payWithPixOrCrypto', 'Pay with Pix or Crypto')}
              </Button>
              <Button
                variant="contained"
                disabled={isProceedDisabled}
                translate="no"
                className="notranslate"
                startIcon={isRedirecting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null}
                sx={{
                  minWidth: 220,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: isProceedDisabled ? "#e0e7ef" : "#2563EB",
                  color: isProceedDisabled ? "#374151" : "#fff",
                  boxShadow: isProceedDisabled
                    ? "none"
                    : "0px 18px 40px rgba(37, 99, 235, 0.45)",
                  border: isProceedDisabled ? "1px solid #c3d0e0" : undefined,
                  opacity: isProceedDisabled ? 0.95 : 1,
                  "&:hover": !isProceedDisabled
                    ? {
                      bgcolor: "#1D4ED8",
                      boxShadow:
                        "0px 22px 50px rgba(37, 99, 235, 0.55)",
                    }
                    : {},
                }}
                onClick={() => setPayWithCard(true)}
              >
                <span translate="no" className="notranslate">
                  {isRedirecting
                    ? t('checkout.redirecting', 'Redirecting…')
                    : t('checkout.payWithCard', 'Pay with card')}
                </span>
              </Button>
            </Stack>

            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={0.75}
              sx={{ mt: 1.5 }}
            >
              <IoLockClosedOutline size={14} />
              <Typography
                variant="caption"
                sx={{ color: "#6B7280", fontSize: 11 }}
              >
                {t(
                  "checkout.secure",
                  "Secure payment powered by Neuro-Exchange"
                )}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    );
  };

  const renderNoId = () => {
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 2 }}>
        <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 5, boxShadow: '0 8px 32px rgba(15,23,42,0.13)', bgcolor: '#fff', p: { xs: 2.5, sm: 4 }, mx: 'auto' }}>
          <CardContent>
            <Stack alignItems="center" spacing={3}>
              <Box sx={{ width: 80, height: 80, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F3F4F6', borderRadius: '50%' }}>
                <IoLockClosedOutline size={40} color="#2563EB" />
              </Box>
              {accountContext.legalStatus.pendingApplication === true || ["created", "pending", "under review", "in review"].includes(String(accountContext.legalIdState || "").trim().toLowerCase()) ? (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#17212F', mb: 1, textAlign: 'center' }}>{t('purchaseApproval.pendingTitle', 'Your personal ID application is being reviewed')}</Typography>
                  <Typography variant="body1" sx={{ color: '#526174', textAlign: 'center', mb: 2 }}>{t('purchaseApproval.pendingMessage', 'You can keep browsing and adding items to your cart. Checkout will be available once your personal ID is approved.')}</Typography>
                </>
              ) : (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#17212F', mb: 1, textAlign: 'center' }}>{t('purchaseApproval.applicationTitle', 'Apply for a personal ID to purchase')}</Typography>
                  <Typography variant="body1" sx={{ color: '#526174', textAlign: 'center', mb: 2 }}>{t('purchaseApproval.applicationMessage', 'A Neuro Access account by itself is not enough to purchase. If you have already applied for a personal ID, please wait for approval. If not, open Neuro Access and tap the Apply for a personal ID banner on the home screen shown below.')}</Typography>
                  <Box component="img" src={`${process.env.PUBLIC_URL}/images/neuro-access-apply-personal-id.jpeg`} alt={t('purchaseApproval.applicationScreenshotAlt', 'Neuro Access home screen showing the Apply for a personal ID banner')} sx={{ width: 150, maxHeight: 322, objectFit: 'contain', borderRadius: 2, boxShadow: '0 5px 16px rgba(20, 50, 64, 0.12)' }} />
                </>
              )}
              <Typography variant="caption" sx={{ color: '#718096', textAlign: 'center' }}>{t('purchaseApproval.statusHelp', 'After approval, sign in again to refresh your status and continue your purchase.')}</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderNoAccount = () => {
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 2 }}>
        <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 5, boxShadow: '0 8px 32px rgba(15,23,42,0.13)', bgcolor: '#fff', p: { xs: 2.5, sm: 4 }, mx: 'auto' }}>
          <CardContent>
            <Stack alignItems="center" spacing={3}>
              <Box sx={{ width: 80, height: 80, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F3F4F6', borderRadius: '50%' }}>
                <IoLockClosedOutline size={40} color="#2563EB" />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2563EB', mb: 1, textAlign: 'center' }}>{t('checkout.needLogin', 'You need to be logged in')}</Typography>
              <Typography variant="body1" sx={{ color: '#6B7280', textAlign: 'center', mb: 2 }}>{t('checkout.needLoginDesc', 'Please log in to your account to continue with checkout and payment.')}</Typography>
              <Button
                variant="contained"
                sx={{ borderRadius: 999, minWidth: 180, fontWeight: 600, bgcolor: '#2563EB', color: '#fff', boxShadow: '0 4px 16px rgba(37,99,235,0.13)', fontSize: '1rem', py: 1.2 }}
                onClick={() => {
                  try { window.localStorage.setItem('pendingCheckout', 'true'); } catch { /* ignore */ }
                  window.location.href = '/auth';
                }}
              >
                {t('checkout.login', 'Log in')}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  };
  // Full-page clean layout to ensure footer stays at bottom and page feels trustworthy
  return (
    <>

      <Global styles={{
        '.MuiButton-root.Mui-disabled': {
          color: '#374151 !important',
        },
      }} />
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#EEF2F7",
          background:
            "radial-gradient(circle at top, rgba(255,255,255,0.9) 0, #EEF2F7 55%, #E5E7EB 100%)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            px: 2,
            pt: 10,
            pb: 8,
          }}
        >
          <Stack
            alignItems={"center"}
            gap={2}
            sx={{ width: "100%", maxWidth: 1200 }}
          >
            {
              (!(accountContext.isLoggedIn ||
                accountContext.isQuickLoggedIn ||
                accountContext.sessionAuth ||
                (typeof window !== "undefined" &&
                  (window.sessionStorage.getItem("sessionAuth") === "true" ||
                    window.sessionStorage.getItem("isQuickLoggedIn") === "true" ||
                    window.sessionStorage.getItem("isLoggedIn") === "true"))) &&
                renderNoAccount()) ||
              (!(String(accountContext.legalIdState || "").trim().toLowerCase() === "approved" &&
                (accountContext.legalId ||
                  (typeof window !== "undefined" &&
                    (localStorage.getItem("agent-legal-id") ||
                      window.sessionStorage.getItem("agent-legal-id"))))) &&
                renderNoId()) ||
              renderCheckout()
            }
          </Stack>
        </Box>
      </Box>
      {
        payWithCard && <CardCheckoutPopup onClose={() => setPayWithCard(false)} />
      }
    </>
  );
}
